import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { Grid, Button, Typography, withStyles, 	Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import ReactSlider from 'react-slider';
import _ from 'lodash';
import ZoomOutIcon from '../../components/icons/zoomout';
import ZoomInIcon from '../../components/icons/zoomin';
import horizontalLine from '../../components/icons/bkg.svg';
import i18n from 'i18n/i18n';

const { loc: { localize }} = i18n;

const MAX_SCALE = 2;
const MAX_FRAME_DISPLAY_SIZE = 200;
const JPEG_EXPORT_QUALITY = 0.9;

const styles = theme => ({
	dialogPaper: {
		minWidth: 444
	},	
	content: {
		width: '100%',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '16px',
		touchAction: 'none'
	},
	dialogTitle: {
		textAlign: 'left'
	},
	prompt: {
		alignSelf: 'flex-start',
		fontSize: '16px'
	},
	cropper: {
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		position: 'relative',
		maxWidth: '100% !important'
	},
	cropperImage: {
		position: 'absolute',
		pointerEvents: 'auto',
		cursor: 'move'
	},
	cropperMask: {
		boxSizing: 'content-box',
		zIndex: 1,
		borderStyle: 'solid',
		borderColor: 'rgba(255, 255, 255, 0.67)',
		pointerEvents: 'none',

		'&::before': {
			content: '""',
			position: 'absolute',
			border: '1px dashed black',
			inset: `${MAX_FRAME_DISPLAY_SIZE * (MAX_SCALE - 1) / 2}px`
		}
	},
	zoomContainer: {
		width: '100%',
		gap: '16px',
		paddingBottom: '16px'
	},
	
	sliderGrid: {
		flex: '1 0 auto'
	},
	slider: {
		height: '26px',
		background: `no-repeat url(${horizontalLine}) center`,
		backgroundSize: 'contain'
	},
	sliderStick: {
		border: '1px black solid'
	},
	handle: {
		width: '12px',
		backgroundColor: '#2fa5e7',
		height: '12px',
		borderRadius: '6px'
	},
	button: {
		borderRadius: '5px',
		marginRight: '30px',
		marginBottom: '12px'
	},
	actionButtons: {
		justifyContent: 'flex-end'
	}
});

class SmartFieldPhotoCropper extends Component {
	constructor(props) {
		super(props);
		this.state = {
			scale: props.scale || 1,
			translate: props.translate || [0, 0],
			imgDisplayWidth: 0,
			imgDisplayHeight: 0
		};
		this.frameDisplayWidth = Math.min(MAX_FRAME_DISPLAY_SIZE, MAX_FRAME_DISPLAY_SIZE);
		this.frameDisplayHeight = Math.min(MAX_FRAME_DISPLAY_SIZE, MAX_FRAME_DISPLAY_SIZE);
		this.dragStartTranslate = null;
		this.dragStartPos = null;
		this.maxTranslate = [0, 0];
		this.imgRef = createRef();
		this.onSaveRef = { current: null };
		this.photoUrl = props.photoUrl;
		this.onCropCb = props.onCropCb;

		this.onSaveRef.current = () => {
			const img = this.imgRef.current;
			const { naturalWidth: imgNaturalWidth, naturalHeight: imgNaturalHeight } = img;
			const imageAspect = imgNaturalWidth / imgNaturalHeight;

			// Assume frameAspect is 1 (square).
			// Output size should therefore also be square, at 1:1 pixel size, considering any scaling.
			// Note that this calculation should be generalised in case frameAspect is not 1.
			const outputSize = Math.min(imgNaturalWidth, imgNaturalHeight) / this.state.scale;
			const frameNaturalSize = [outputSize, outputSize];

			const canvas = document.createElement('canvas');
			canvas.width = frameNaturalSize[0];
			canvas.height = frameNaturalSize[1];

			const context = canvas.getContext('2d');

			// translate is in screen pixels; naturalTranslate in frame/output pixels.
			let naturalTranslate = [...this.state.translate];
			naturalTranslate[0] *= (frameNaturalSize[0] / this.frameDisplayWidth);
			naturalTranslate[1] *= (frameNaturalSize[1] / this.frameDisplayHeight); // Same factor as previous line.

			const frameAspect = 1;
			let baseScale;
			let sx;
			let sy;
			let sWidth;
			let sHeight;
			if (imageAspect > frameAspect) {
				// Scale of 1 fits image natural height to frame natural height.
				baseScale = frameNaturalSize[1] / imgNaturalHeight;
				sHeight = imgNaturalHeight / this.state.scale;
				sWidth = (imgNaturalHeight * frameAspect) / this.state.scale;
			} else {
				// Scale of 1 fits image natural width to frame natural width.
				baseScale = frameNaturalSize[0] / imgNaturalWidth;
				sWidth = imgNaturalWidth / this.state.scale;
				sHeight = (imgNaturalWidth / frameAspect) / this.state.scale;
			}
			sx = (imgNaturalWidth / 2) - (naturalTranslate[0] / baseScale) -
				(((frameNaturalSize[0] / 2) / baseScale) / this.state.scale);
			sy = (imgNaturalHeight / 2) - (naturalTranslate[1] / baseScale) -
				(((frameNaturalSize[1] / 2) / baseScale) / this.state.scale);

			let dx = 0;
			let dy = 0;
			let dWidth = canvas.width;
			let dHeight = canvas.height;
			context && context.drawImage(img,
				sx, sy, sWidth, sHeight,
				dx, dy, dWidth, dHeight
			);

			const matchResult = this.photoUrl.match(/^data:(.*);base64/);
			const originalJpeg = matchResult && matchResult[1] === 'image/jpeg';
			canvas.toBlob(
				blob => {
					this.onCropCb && this.onCropCb({
						blob,
						extension: originalJpeg ? 'jpeg' : 'png',
						scale: this.state.scale,
						translate: this.state.translate
					});
					this.closeDialog();
				},
				originalJpeg ? 'image/jpeg' : 'image/png',
				JPEG_EXPORT_QUALITY
			);
		};
	}

	closeDialog = () => this.props.hideModal();

	handleEscape = (e) => {
		// Prefer modern key; fallback for older browsers
		const isEsc = e.key === 'Escape' || e.keyCode === 27;
		if (!isEsc) return;

		// If something *intentionally* prevented default, honor it
		if (e.defaultPrevented) return;

		this.closeDialog();
	};

	componentDidUpdate(_, prevState) {
		if (
			prevState.imgDisplayWidth !== this.state.imgDisplayWidth ||
			prevState.imgDisplayHeight !== this.state.imgDisplayHeight ||
			prevState.scale !== this.state.scale
		) {
			const { imgDisplayWidth, imgDisplayHeight, scale } = this.state;
			if (!(imgDisplayWidth && imgDisplayHeight)) return;
			this.maxTranslate = [
				((imgDisplayWidth * scale) - this.frameDisplayWidth) / 2 / scale,
				((imgDisplayHeight * scale) - this.frameDisplayHeight) / 2 / scale
			];
			this.setTranslateClamped(this.state.translate);
		}
	}

	componentDidMount() {
		window.addEventListener('keydown', this.handleEscape);
	}

	componentWillUnmount() {
		this.dragStartTranslate = null;
		this.dragStartPos = null;
		window.removeEventListener('keydown', this.handleEscape);
	}

	setImgDisplaySize = ({ naturalWidth, naturalHeight }) => {
		const frameAspect = 1;
		const imageAspect = naturalWidth / naturalHeight;
		if (imageAspect > frameAspect) {
			this.setState({
				imgDisplayHeight: this.frameDisplayHeight,
				imgDisplayWidth: this.frameDisplayHeight * imageAspect
			});
		} else {
			const displayHeight = this.frameDisplayWidth / imageAspect;
			this.setState({
				imgDisplayWidth: this.frameDisplayWidth,
				imgDisplayHeight: this.frameDisplayWidth / imageAspect
			});

			if (this.props.translate === undefined) {
				// P9052-27544 Default to top aligned position
				const maxTranslateY = ((displayHeight * this.state.scale - this.frameDisplayHeight) / 2 / this.state.scale);
				this.setState({
					translate: [0, maxTranslateY]
				});
			}
		}
	};

	setTranslateClamped = (newTranslate) => {
		const clamped = [
			_.clamp(newTranslate[0], -this.maxTranslate[0], this.maxTranslate[0]),
			_.clamp(newTranslate[1], -this.maxTranslate[1], this.maxTranslate[1])
		];
		this.setState({ translate: clamped });
	};

	translateHandler = {
		onStart: (clientX, clientY) => {
			this.dragStartTranslate = this.state.translate;
			this.dragStartPos = [clientX, clientY];
		},
		onMove: (clientX, clientY) => {
			if (this.dragStartPos) {
				const deltaX = (clientX - this.dragStartPos[0]) / this.state.scale;
				const deltaY = (clientY - this.dragStartPos[1]) / this.state.scale;
				this.setTranslateClamped([
					this.dragStartTranslate[0] + deltaX,
					this.dragStartTranslate[1] + deltaY
				]);
			}
		},
		onEnd: () => {
			this.dragStartTranslate = null;
			this.dragStartPos = null;
		}
	};

	reset = () => {
		this.setState({ translate: [0, 0], scale: 1 });
	};

	render() {
		const { classes, photoUrl } = this.props;
		const { scale, translate, imgDisplayWidth, imgDisplayHeight } = this.state;

		const imageStyle = {
			width: imgDisplayWidth,
			height: imgDisplayHeight,
			transform: `scale(${scale}) translate(${translate[0]}px, ${translate[1]}px)`
		};

		// P9052-27546 Add parameter to photoUrl to prevent using a cached non-CORS version of the image
		const cropPhotoUrl = typeof photoUrl === 'string' && photoUrl.startsWith('http')
			? `${photoUrl}${photoUrl.includes('?') ? '&' : '?'}crop=true`
			: photoUrl;

		return (
			<Dialog
				open={true}
				onClose={this.closeDialog}
				onEscapeKeyDown={this.closeDialog}
				disableEscapeKeyDown={false}
				BackdropProps={{ style: { backgroundColor: 'rgba(46, 63, 79, 0.4)' } }}
				classes={{ paper: classes.dialogPaper }}
				PaperProps={{
					style: {
						border: 0,
						width: 309,
						minWidth: 505,
						borderRadius: 5,
						boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.08)'
					}
				}}
			>
				<DialogTitle
					variant='body1'
					color='default'
					className={classes.dialogTitle}>{localize('smartfield-photo-picker-crop-photo')}
				</DialogTitle>
				<DialogContent className={classes.dialogContent} >
					<Grid>
						<Grid
							container
							direction="column"
							className={classes.content}
							onMouseMove={e => this.translateHandler.onMove(e.clientX, e.clientY)}
							onTouchMove={e => this.translateHandler.onMove(e.touches[0].clientX, e.touches[0].clientY)}
							onMouseUp={this.translateHandler.onEnd}
							onTouchEnd={this.translateHandler.onEnd}
							onDoubleClick={this.reset}
						>
							<Typography className={classes.prompt}>
								{localize('smartfield-photo-picker-crop-photo-drag-prompt')}
							</Typography>
							<Grid
								item
								container
								className={classes.cropper}
								style={{
									width: this.frameDisplayWidth * MAX_SCALE,
									height: this.frameDisplayHeight * MAX_SCALE
								}}
							>
								<img
									ref={this.imgRef}
									className={classes.cropperImage}
									style={imageStyle}
									src={cropPhotoUrl}
									crossOrigin="use-credentials"
									onLoad={e => this.setImgDisplaySize(e.target)}
									onMouseDown={e => {
										this.translateHandler.onStart(e.clientX, e.clientY);
										e.preventDefault();
									}}
									onTouchStart={e => this.translateHandler.onStart(e.touches[0].clientX, e.touches[0].clientY)}
									onContextMenu={e => e.preventDefault()}
									onWheel={e => {
										e.persist();
										this.setState(prev => ({
											scale: _.clamp(prev.scale - (e.deltaY / 100), 1, MAX_SCALE)
										}));
									}}
									data-tid="smartfield-photo-cropper-photo"
								/>
								<div
									className={classes.cropperMask}
									style={{
										width: this.frameDisplayWidth,
										height: this.frameDisplayHeight,
										minWidth: this.frameDisplayWidth,
										borderWidth: `${this.frameDisplayHeight * (MAX_SCALE - 1) / 2}px ${
											this.frameDisplayWidth * (MAX_SCALE - 1) / 2}px`
									}}
								/>
							</Grid>
							<Grid item container className={classes.zoomContainer}>
								<Grid item>
									<ZoomOutIcon />
								</Grid>
								<Grid item className={classes.sliderGrid}>
									<ReactSlider
										className={classes.slider}
										handleClassName={classes.handle}
										value={scale}
										defaultValue={50}
										min={1}
										max={MAX_SCALE}
										step={0.01}
										onChange={(val) => this.setState({ scale: val })}
									>
									</ReactSlider>
								</Grid>
								<Grid item>
									<ZoomInIcon />
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>
				<Grid item container className={classes.actionButtons}>
					<Button
						variant='outlined'
						color='primary'
						data-tid="button-photo-cropper-modal-cancel"
						className={classes.button}
						onClick={() => this.closeDialog()}
						disabled={false}>
						{localize('cancel')}
					</Button>
					<Button
						variant='contained'
						color='primary'
						data-tid="button-photo-cropper-modal-save"
						className={classes.button}
						onClick={() => this.onSaveRef.current && this.onSaveRef.current()}
						disabled={false}>
						{localize('save')}
					</Button>
				</Grid>
			</Dialog>
		);
	}
}

SmartFieldPhotoCropper.propTypes = {
	scale: PropTypes.number,
	translate: PropTypes.object,
	photoUrl: PropTypes.string,
	onCropCb: PropTypes.func,
	classes: PropTypes.object,
	hideModal: PropTypes.func
};

export default withStyles(styles)(SmartFieldPhotoCropper);


// WEBPACK FOOTER //
// ./src/components/SmartFieldPhotoPicker/SmartFieldPhotoCropper.js